/**
 * End portal rules and the eye of ender's flight (pure; no DOM, usable from
 * smoke tests).
 *
 *  - insertEye: right-clicking an empty End Portal Frame with an eye fills it;
 *    once all twelve frames of a ring (3 x 3 opening, corners absent) hold eyes
 *    the opening is filled with End Portal blocks.
 *  - EnderEye: vanilla EyeOfEnder's signal flight — it rises and travels up to 12
 *    blocks toward the target (or sinks into it when closer), hovers, and after
 *    80 ticks drops as an item (80%) or shatters (20%).
 */

import { END_PORTAL_FRAME_EYE_ID, END_PORTAL_FRAME_ID, END_PORTAL_ID } from '../blocks';

export interface BlockAccess {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): unknown;
}

/** The twelve frame offsets round a portal centre (the 3 x 3 opening's middle). */
export const FRAME_RING: ReadonlyArray<readonly [number, number]> = (() => {
  const out: Array<[number, number]> = [];
  for (let d = -1; d <= 1; d++) out.push([d, -2], [d, 2], [-2, d], [2, d]);
  return out;
})();

/** Do all twelve frames round centre (cx, y, cz) hold an eye? */
export function ringComplete(w: Pick<BlockAccess, 'getBlock'>, cx: number, y: number, cz: number): boolean {
  return FRAME_RING.every(([dx, dz]) => w.getBlock(cx + dx, y, cz + dz) === END_PORTAL_FRAME_EYE_ID);
}

/** Fill the 3 x 3 opening with end portal blocks. */
export function fillPortal(w: Pick<BlockAccess, 'setBlock'>, cx: number, y: number, cz: number): void {
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) w.setBlock(cx + dx, y, cz + dz, END_PORTAL_ID);
}

/**
 * Put an eye into the empty frame at (x, y, z). Returns 'none' when it is not an
 * empty frame, 'filled' for a normal insertion and 'activated' when it completed
 * a ring (the portal is now open).
 */
export function insertEye(w: BlockAccess, x: number, y: number, z: number): 'none' | 'filled' | 'activated' {
  if (w.getBlock(x, y, z) !== END_PORTAL_FRAME_ID) return 'none';
  w.setBlock(x, y, z, END_PORTAL_FRAME_EYE_ID);
  // the frame can belong to any ring whose offset set contains it
  for (const [dx, dz] of FRAME_RING) {
    const cx = x - dx, cz = z - dz;
    if (ringComplete(w, cx, y, cz)) { fillPortal(w, cx, y, cz); return 'activated'; }
  }
  return 'filled';
}

/** Is this world cell standing inside an end portal (feet or head)? */
export function inEndPortal(w: Pick<BlockAccess, 'getBlock'>, x: number, y: number, z: number): boolean {
  return w.getBlock(x, y, z) === END_PORTAL_ID || w.getBlock(x, y + 1, z) === END_PORTAL_ID;
}

/** Engine y an eye sinks to when it is above its stronghold (the portal room's depth). */
export const EYE_SINK_Y = 40;
export const EYE_LIFE = 80;
export const EYE_RANGE = 12;

export class EnderEye {
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  age = 0;
  /** True once it is time to drop / shatter. */
  done = false;
  readonly drops: boolean;
  private readonly tx: number;
  private readonly ty: number;
  private readonly tz: number;

  /** `target` is the stronghold's (x, z); `rnd` decides drop vs shatter. */
  constructor(x: number, y: number, z: number, target: { x: number; z: number }, rnd: () => number) {
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    const dx = target.x - x, dz = target.z - z;
    const dist = Math.hypot(dx, dz);
    if (dist > EYE_RANGE) {
      this.tx = x + (dx / dist) * EYE_RANGE; this.tz = z + (dz / dist) * EYE_RANGE; this.ty = y + 8;
    } else {
      this.tx = target.x + 0.5; this.tz = target.z + 0.5; this.ty = Math.min(y, EYE_SINK_Y);
    }
    this.drops = rnd() < 0.8;
  }

  /** One 20 Hz tick. */
  tick(): void {
    if (this.done) return;
    this.px = this.x; this.py = this.y; this.pz = this.z;
    this.age++;
    const k = 0.1;
    this.x += (this.tx - this.x) * k;
    this.z += (this.tz - this.z) * k;
    // a gentle bob while it hovers over a far target
    this.y += (this.ty - this.y) * k + (this.age > 30 ? Math.sin(this.age * 0.4) * 0.03 : 0);
    if (this.age >= EYE_LIFE) this.done = true;
  }
}
